import React, { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Check, X, User, Mail, Phone, Calendar, Clock } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API_URL } from '../config/api';

const API = API_URL;

const ApprovalReviewDialog = ({ open, onOpenChange, requests, teamMembers, onApprovalComplete }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const currentRequest = requests[currentIndex];

  const getTeamMemberData = (teamMemberId) => {
    return teamMembers.find(c => c.id === teamMemberId);
  };

  // Check if a value has actually changed
  const hasChanged = (oldValue, newValue) => {
    // Handle arrays (for unavailableDates, recurringAvailability)
    if (Array.isArray(oldValue) && Array.isArray(newValue)) {
      if (oldValue.length !== newValue.length) return true;
      return JSON.stringify(oldValue.sort()) !== JSON.stringify(newValue.sort());
    }
    // Handle strings/primitives
    return oldValue !== newValue;
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleApprove = async () => {
    setProcessing(true);
    try {
      await axios.put(`${API}/change-requests/${currentRequest.id}/approve`);
      toast.success(`Approved changes for ${currentRequest.teamMemberName}`);
      
      // Move to next request or close
      if (currentIndex < requests.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        onOpenChange(false);
        setCurrentIndex(0);
      }
      
      if (onApprovalComplete) {
        onApprovalComplete();
      }
    } catch (error) {
      console.error("Error approving request:", error);
      toast.error("Failed to approve request");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    setProcessing(true);
    try {
      await axios.put(`${API}/change-requests/${currentRequest.id}/reject`);
      toast.success(`Rejected changes for ${currentRequest.teamMemberName}`);
      
      // Move to next request or close
      if (currentIndex < requests.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        onOpenChange(false);
        setCurrentIndex(0);
      }
      
      if (onApprovalComplete) {
        onApprovalComplete();
      }
    } catch (error) {
      console.error("Error rejecting request:", error);
      toast.error("Failed to reject request");
    } finally {
      setProcessing(false);
    }
  };

  if (!currentRequest) return null;

  const teamMember = getTeamMemberData(currentRequest.teamMemberId);
  const changes = currentRequest.changes || {};

  const formatDateList = (dates) => {
    if (!dates || dates.length === 0) return "None";
    return dates.slice(0, 3).join(", ") + (dates.length > 3 ? `... (+${dates.length - 3} more)` : "");
  };

  const formatAvailability = (availability) => {
    if (!availability || availability.length === 0) return "None";
    return availability.map(a => `${a.day}: ${a.startTime}-${a.endTime}`).join(", ");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl">Review Change Request</DialogTitle>
            <Badge variant="secondary">
              {currentIndex + 1} of {requests.length}
            </Badge>
          </div>
          <DialogDescription>
            Review and approve or reject profile changes for {currentRequest.teamMemberName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* TeamMember Info */}
          <div className="flex items-center space-x-3 p-4 bg-accent rounded-lg">
            <User className="h-8 w-8 text-primary" />
            <div>
              <p className="font-semibold text-lg">{currentRequest.teamMemberName}</p>
              <p className="text-sm text-muted-foreground">
                Submitted {new Date(currentRequest.submittedAt).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Changes Comparison - ONLY SHOW ACTUAL CHANGES */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="font-semibold text-lg">Requested Changes</h3>
              <Badge variant="outline" className="text-xs">
                {Object.keys(changes).filter(key => {
                  const teamMember = getTeamMemberData(currentRequest.teamMemberId);
                  return hasChanged(teamMember?.[key], changes[key]);
                }).length} change{Object.keys(changes).filter(key => {
                  const teamMember = getTeamMemberData(currentRequest.teamMemberId);
                  return hasChanged(teamMember?.[key], changes[key]);
                }).length !== 1 ? 's' : ''}
              </Badge>
            </div>

            {/* Email Change - ONLY if actually different */}
            {changes.email && hasChanged(teamMember?.email, changes.email) && (
              <div className="grid grid-cols-2 gap-4 p-4 border-2 border-orange-200 rounded-lg bg-orange-50">
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Current Email</span>
                  </div>
                  <p className="text-sm pl-6">{teamMember?.email || "N/A"}</p>
                </div>
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <Mail className="h-4 w-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-600">New Email →</span>
                  </div>
                  <p className="text-sm pl-6 font-bold text-orange-900">{changes.email}</p>
                </div>
              </div>
            )}

            {/* Phone Change - ONLY if actually different */}
            {changes.phone && hasChanged(teamMember?.phone, changes.phone) && (
              <div className="grid grid-cols-2 gap-4 p-4 border-2 border-orange-200 rounded-lg bg-orange-50">
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Current Phone</span>
                  </div>
                  <p className="text-sm pl-6">{teamMember?.phone || "N/A"}</p>
                </div>
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <Phone className="h-4 w-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-600">New Phone →</span>
                  </div>
                  <p className="text-sm pl-6 font-bold text-orange-900">{changes.phone}</p>
                </div>
              </div>
            )}

            {/* Recurring Availability Change - ONLY if actually different */}
            {changes.recurringAvailability && hasChanged(teamMember?.recurringAvailability, changes.recurringAvailability) && (
              <div className="p-4 border-2 border-orange-200 rounded-lg bg-orange-50">
                <h4 className="font-semibold text-orange-900 mb-3 flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Recurring Availability Change
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Current:</span>
                    <div className="mt-2 space-y-1">
                      {teamMember?.recurringAvailability?.length > 0 ? (
                        teamMember.recurringAvailability.map((a, i) => (
                          <div key={i} className="text-xs bg-white p-2 rounded">
                            {a.day}: {a.startTime}-{a.endTime}
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-muted-foreground">None set</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-orange-600">New →</span>
                    <div className="mt-2 space-y-1">
                      {changes.recurringAvailability.map((a, i) => (
                        <div key={i} className="text-xs bg-orange-200 p-2 rounded font-bold text-orange-900">
                          {a.day}: {a.startTime}-{a.endTime}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Unavailable Dates Change - HIGHLIGHTED & EXPANDED - SUPER IMPORTANT */}
            {changes.unavailableDates && hasChanged(teamMember?.unavailableDates, changes.unavailableDates) && (
              <div className="p-4 border-2 border-red-300 rounded-lg bg-red-50">
                <h4 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  ⚠️ Unavailable Dates Change - IMPORTANT
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Current Unavailable:</span>
                    <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                      {teamMember?.unavailableDates?.length > 0 ? (
                        teamMember.unavailableDates.map((date, i) => (
                          <div key={i} className="text-xs bg-white p-2 rounded">
                            {date}
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-muted-foreground">None</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-red-700">New Unavailable →</span>
                    <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                      {changes.unavailableDates.map((date, i) => (
                        <div key={i} className="text-xs bg-red-200 p-2 rounded font-bold text-red-900">
                          {date}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-red-700 mt-3 font-medium">
                  📌 TeamMember will be unavailable on these dates. Check conflicts with scheduled trips!
                </p>
              </div>
            )}

            {/* No actual changes message */}
            {!Object.keys(changes).some(key => hasChanged(teamMember?.[key], changes[key])) && (
              <div className="p-8 text-center bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  No actual changes detected. All values are the same as current.
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              onClick={handleReject}
              variant="outline"
              className="flex-1"
              disabled={processing}
            >
              <X className="h-4 w-4 mr-2" />
              Reject
            </Button>
            <Button
              onClick={handleApprove}
              className="flex-1"
              disabled={processing}
            >
              <Check className="h-4 w-4 mr-2" />
              Approve Changes
            </Button>
          </div>

          {/* Navigation */}
          {requests.length > 1 && (
            <div className="flex justify-between items-center pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                disabled={currentIndex === 0 || processing}
              >
                Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentIndex(Math.min(requests.length - 1, currentIndex + 1))}
                disabled={currentIndex === requests.length - 1 || processing}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ApprovalReviewDialog;
